<template>
    <div class="max-w-6xl mx-auto pb-6 space-y-6">
        <h1 class="text-2xl font-bold text-gray-800">Minha Conta</h1>

        <!-- Dados do usuário -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 class="text-base font-semibold text-gray-500 mb-4">Dados pessoais</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Nome</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ authInfo.name || '—' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Email</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ authInfo.email || '—' }}</p>
                </div>
            </div>
        </section>

        <!-- Dispositivos conectados -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 class="text-base font-semibold text-gray-500 mb-1">Dispositivos conectados</h2>
            <p class="text-xs text-gray-400 mb-4">Máximo de {{ maxDevices }} dispositivos simultâneos. O mais antigo é desconectado automaticamente ao fazer login em um novo.</p>

            <div v-if="loadingSessions" class="space-y-2">
                <div v-for="i in 2" :key="i" class="skeleton h-14 w-full rounded-lg"></div>
            </div>

            <div v-else class="divide-y divide-gray-100">
                <div
                    v-for="session in sessions"
                    :key="session.device_id"
                    class="flex items-center justify-between py-3 gap-2"
                    :class="{ 'bg-cyan-50 -mx-2 px-2 rounded-lg': session.device_id === currentDeviceId }"
                >
                    <div class="min-w-0">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold text-gray-700">{{ session.device_name || 'Dispositivo desconhecido' }}</span>
                            <span v-if="session.device_id === currentDeviceId" :class="adminBadgeClass('info')">Este dispositivo</span>
                        </div>
                        <p class="text-xs text-gray-400 mt-0.5">Último acesso: {{ formatDateTime(session.last_used_at) }}</p>
                    </div>
                    <button
                        v-if="session.device_id !== currentDeviceId"
                        @click="openLogoutDeviceModal(session)"
                        class="text-xs font-semibold text-red-500 hover:text-red-600 hover:bg-red-50 rounded-md px-2 py-1 shrink-0 transition-colors"
                    >Deslogar</button>
                </div>

                <div v-if="!sessions.length" class="text-sm text-gray-400 py-2">
                    Nenhuma sessão ativa encontrada.
                </div>
            </div>
        </section>

        <!-- Logout device confirm modal -->
        <dialog ref="logoutDeviceModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg" v-if="deviceToLogout">
                <h3 class="font-bold text-lg text-gray-800">Desconectar dispositivo?</h3>
                <p class="py-3 text-sm text-gray-600">
                    <strong>{{ deviceToLogout.device_name || 'Esse dispositivo' }}</strong> vai ser deslogado e vai precisar entrar novamente pra acessar o sistema.
                </p>
                <p v-if="logoutDeviceError" class="text-red-600 text-xs">{{ logoutDeviceError }}</p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeLogoutDeviceModal" :disabled="loggingOutDevice">Cancelar</button>
                    <button
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !text-white !bg-red-600 hover:!bg-red-700"
                        :disabled="loggingOutDevice"
                        @click="confirmLogoutDevice"
                    >
                        {{ loggingOutDevice ? 'Desconectando...' : 'Desconectar' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>

        <!-- Dados da empresa -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-base font-semibold text-gray-500">Dados da empresa</h2>
                <button
                    v-if="canEditCompany && !editingCompany"
                    @click="startEditCompany"
                    class="btn btn-sm btn-ghost text-cyan-600"
                >Editar</button>
            </div>

            <div class="flex flex-col items-center gap-y-2 mb-6">
                <label
                    v-if="canEditCompany"
                    for="companyLogoUpload"
                    class="relative inline-block rounded-lg group"
                    :class="uploadingLogo ? 'cursor-wait' : 'cursor-pointer'"
                >
                    <img v-if="company.logo_url" :src="company.logo_url" class="w-20 h-20 object-contain rounded-lg bg-gray-50 border border-gray-100" alt="Logo da empresa">
                    <div v-else class="w-20 h-20 rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-300 text-center px-1">Sem logo</div>
                    <div
                        class="absolute inset-0 rounded-lg flex items-center justify-center bg-black/0 transition-colors"
                        :class="uploadingLogo ? 'bg-black/40' : 'group-hover:bg-black/40'"
                    >
                        <svg v-if="uploadingLogo" class="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    <span
                        v-if="!uploadingLogo"
                        class="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-cyan-600 text-white ring-2 ring-white shadow-sm transition-transform group-hover:scale-110"
                    >
                        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16.5A2.5 2.5 0 016.5 14H7a5 5 0 019.7-1.7A3.5 3.5 0 0118 19H6.5A2.5 2.5 0 014 16.5Z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.5 12.5a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0z" />
                        </svg>
                    </span>
                    <input
                        id="companyLogoUpload"
                        type="file"
                        class="hidden"
                        accept="image/*"
                        :disabled="uploadingLogo"
                        @change="onLogoFileChange"
                    />
                </label>

                <template v-else>
                    <img v-if="company.logo_url" :src="company.logo_url" class="w-20 h-20 object-contain rounded-lg bg-gray-50 border border-gray-100" alt="Logo da empresa">
                    <div v-else class="w-20 h-20 rounded-lg border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-300 text-center px-1">Sem logo</div>
                </template>

                <span v-if="canEditCompany" class="text-xs text-gray-400">{{ uploadingLogo ? 'Enviando...' : (company.logo_url ? 'Clique para alterar a logo' : 'Clique para adicionar uma logo') }}</span>
            </div>

            <div v-if="!editingCompany" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Nome</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.name || '—' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Telefone</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.phone || '—' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">WhatsApp</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.whatsapp || '—' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Endereço</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.address || '—' }}{{ company.cep ? ' - ' + company.cep : '' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">Responsável Técnico</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.responsavel_name || '—' }}</p>
                </div>
                <div class="border border-gray-100 rounded-lg p-4">
                    <span class="text-xs uppercase font-semibold text-gray-400">CREFITO</span>
                    <p class="text-sm font-semibold text-gray-800 mt-1">{{ company.responsavel_crefito || '—' }}</p>
                </div>
            </div>

            <form v-else class="grid grid-cols-1 md:grid-cols-2 gap-4" @submit.prevent="saveCompany">
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Nome</label>
                    <input v-model="companyForm.name" class="input input-bordered w-full text-sm">
                </div>
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Telefone</label>
                    <input v-model="companyForm.phone" class="input input-bordered w-full text-sm">
                </div>
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">WhatsApp</label>
                    <input v-model="companyForm.whatsapp" class="input input-bordered w-full text-sm">
                </div>
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">CEP</label>
                    <input v-model="companyForm.cep" class="input input-bordered w-full text-sm">
                </div>
                <div class="md:col-span-2">
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Endereço</label>
                    <input v-model="companyForm.address" class="input input-bordered w-full text-sm">
                </div>
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">Responsável Técnico</label>
                    <input v-model="companyForm.responsavel_name" class="input input-bordered w-full text-sm">
                </div>
                <div>
                    <label class="block text-xs uppercase font-semibold text-gray-400 mb-1">CREFITO</label>
                    <input v-model="companyForm.responsavel_crefito" class="input input-bordered w-full text-sm">
                </div>
                <p class="md:col-span-2 text-xs text-gray-400">Esses dados aparecem nos relatórios em PDF gerados pelo sistema.</p>
                <div class="md:col-span-2 flex justify-end gap-2">
                    <button type="button" class="btn btn-ghost btn-sm" @click="cancelEditCompany" :disabled="savingCompany">Cancelar</button>
                    <button type="submit" class="btn btn-primary btn-sm text-white" :disabled="savingCompany">
                        <span v-if="savingCompany" class="loading loading-spinner loading-xs"></span>
                        Salvar
                    </button>
                </div>
            </form>
        </section>

        <!-- Assinatura -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 class="text-base font-semibold text-gray-500 mb-4">Assinatura</h2>

            <div v-if="loadingSubscription" class="space-y-3">
                <div class="skeleton h-5 w-48 rounded"></div>
                <div class="skeleton h-4 w-64 rounded"></div>
            </div>

            <div v-else-if="!subscription || !subscription.active">
                <div class="flex items-center justify-between gap-4">
                    <div>
                        <template v-if="subscription && subscription.ended">
                            <p class="text-sm text-red-600 font-semibold">
                                Assinatura encerrada em {{ formatDate(subscription.ended.ended_at) }}{{ subscription.ended.reason === 'payment_failed' ? ' — o pagamento foi recusado' : '' }}
                            </p>
                            <p class="text-xs text-gray-500 mt-1">Para voltar a usar, assine novamente e informe um novo cartão no pagamento.</p>
                        </template>
                        <p v-else class="text-sm text-gray-600">
                            <span v-if="subscriptionStatus === 'trial'">Período de teste: <strong>{{ daysLeft }} dia{{ daysLeft !== 1 ? 's' : '' }} restante{{ daysLeft !== 1 ? 's' : '' }}</strong></span>
                            <span v-else-if="subscriptionStatus === 'expired_trial'" class="text-red-600 font-semibold">Período de teste encerrado</span>
                            <span v-else>Sem assinatura ativa</span>
                        </p>
                    </div>
                    <router-link :to="{ name: 'subscription' }" class="btn btn-primary btn-sm text-white shrink-0">
                        {{ subscription && subscription.ended ? 'Assinar novamente' : 'Assinar agora' }}
                    </router-link>
                </div>
            </div>

            <div v-else class="space-y-3">
                <div class="flex flex-col md:flex-row md:items-center gap-x-4 gap-y-2">
                    <div class="flex-1 space-y-1">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold text-gray-700">{{ plan.name }}</span>
                            <span :class="statusBadgeClass">{{ statusLabel }}</span>
                        </div>
                        <p v-if="subscription.plan_amount" class="text-sm text-gray-500">
                            R$ {{ subscription.plan_amount.toFixed(2).replace('.', ',') }}/mês
                        </p>
                        <p v-if="subscription.current_period_end" class="text-xs text-gray-400">
                            {{ subscription.cancel_at_period_end ? 'Cancela em' : 'Renova em' }}:
                            {{ formatDate(subscription.current_period_end) }}
                        </p>
                        <p v-if="subscription.coupon" class="text-xs text-emerald-600 font-semibold">
                            Cupom ativo: {{ formatCouponLabel(subscription.coupon) }}
                        </p>
                    </div>

                    <div class="flex gap-2 shrink-0">
                        <button
                            v-if="subscription.cancel_at_period_end"
                            @click="reactivate"
                            :disabled="loadingAction"
                            class="btn btn-sm btn-success text-white"
                        >
                            <span v-if="loadingAction" class="loading loading-spinner loading-xs"></span>
                            Reativar
                        </button>
                        <button
                            v-else
                            @click="openCancelModal"
                            :disabled="loadingAction"
                            class="btn btn-sm btn-ghost text-red-500 border-red-200"
                        >
                            <span v-if="loadingAction" class="loading loading-spinner loading-xs"></span>
                            Cancelar plano
                        </button>
                    </div>
                </div>

                <!-- past_due: retry payment -->
                <div v-if="subscription.status === 'past_due'" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p class="text-sm text-red-700 font-semibold mb-1">Pagamento recusado</p>
                    <p class="text-xs text-red-600 mb-3">Adicione um novo cartão em "Meios de Pagamento" abaixo — ele passa a ser o padrão e a cobrança é refeita na hora. Se preferir manter o cartão atual, tente novamente.</p>
                    <button @click="retryPay" class="btn btn-sm btn-warning text-white" :disabled="retryingPayment">
                        <span v-if="retryingPayment" class="loading loading-spinner loading-sm"></span>
                        Tentar pagamento novamente
                    </button>
                </div>
            </div>
        </section>

        <!-- Meios de Pagamento -->
        <section v-if="subscription && subscription.active" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-base font-semibold text-gray-500">Meios de Pagamento</h2>
                <button class="btn btn-sm btn-primary text-white" @click="openAddCard">+ Adicionar cartão</button>
            </div>

            <div v-if="loadingPaymentMethods" class="space-y-2">
                <div v-for="i in 2" :key="i" class="skeleton h-12 w-full rounded-lg"></div>
            </div>

            <div v-else-if="paymentMethods.length === 0" class="text-sm text-gray-400 py-2">
                Nenhum cartão cadastrado.
            </div>

            <div v-else class="divide-y divide-gray-100">
                <div v-for="card in paymentMethods" :key="card.id" class="flex items-center justify-between py-3 gap-2">
                    <div class="flex items-center gap-3 min-w-0">
                        <span class="text-xs font-bold text-gray-500 uppercase w-12">{{ card.brand }}</span>
                        <div class="min-w-0">
                            <span class="text-sm font-semibold text-gray-700">•••• {{ card.last4 }}</span>
                            <span class="text-xs text-gray-400 ml-2">{{ String(card.exp_month).padStart(2,'0') }}/{{ String(card.exp_year).slice(-2) }}</span>
                        </div>
                        <span v-if="card.is_default || paymentMethods.length === 1" :class="adminBadgeClass('success')">Padrão</span>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                        <button
                            v-if="!card.is_default && paymentMethods.length > 1"
                            @click="setDefault(card.id)"
                            :disabled="pmActionLoading"
                            class="btn btn-sm btn-ghost text-gray-600 whitespace-nowrap"
                        >Definir padrão</button>
                        <button
                            v-if="!isLastCardOnActiveSubscription(card)"
                            @click="removeCard(card.id)"
                            :disabled="pmActionLoading"
                            class="btn btn-sm btn-outline btn-error whitespace-nowrap"
                        >Remover</button>
                        <span
                            v-else
                            class="text-xs text-gray-400 whitespace-nowrap"
                            title="Mantenha ao menos um cartão enquanto há assinatura ativa"
                        >Único cartão</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Histórico de Cobranças -->
        <section v-if="subscription && subscription.active" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 class="text-base font-semibold text-gray-500 mb-4">Histórico de Cobranças</h2>

            <div v-if="loadingInvoices" class="text-sm text-gray-400">Carregando...</div>

            <div v-else-if="invoices.length === 0" class="text-sm text-gray-400 py-2">
                Nenhuma cobrança registrada.
            </div>

            <div v-else class="divide-y divide-gray-100">
                <div v-for="inv in invoices" :key="inv.id" class="flex items-center justify-between py-3 gap-2 text-sm">
                    <div class="min-w-0">
                        <p class="text-gray-700 font-semibold">{{ formatDate(inv.created) }}</p>
                        <p v-if="inv.description" class="text-xs text-gray-400 truncate max-w-xs">{{ inv.description }}</p>
                    </div>
                    <div class="flex items-center gap-3 shrink-0">
                        <span class="font-semibold text-gray-800">R$ {{ (inv.amount_paid || inv.amount_due || 0).toFixed(2).replace('.', ',') }}</span>
                        <span :class="invoiceStatusClass(inv.status)">{{ invoiceStatusLabel(inv.status) }}</span>
                        <a
                            v-if="inv.invoice_pdf"
                            :href="inv.invoice_pdf"
                            target="_blank"
                            rel="noopener"
                            title="Baixar PDF"
                            class="inline-flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700 hover:underline"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            PDF
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <AddCardModal ref="addCardModalRef" @card-added="onCardAdded" />

        <!-- Cancel plan confirm modal -->
        <dialog ref="cancelModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg">
                <h3 class="font-bold text-lg text-gray-800">Cancelar sua assinatura?</h3>
                <p class="py-3 text-sm text-gray-600">
                    Ao cancelar, você perde acesso ao Sirimmatrix e todos os benefícios do seu plano
                    (relatórios com IA, análises ACIA, matriz de risco, etc.) — não é possível desfazer, mas
                    você pode assinar de novo quando quiser.
                </p>
                <p v-if="subscription && subscription.current_period_end" class="text-sm font-semibold text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Seu acesso continua normalmente até <strong>{{ formatDate(subscription.current_period_end) }}</strong> — a data final do período já pago.
                </p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeCancelModal" :disabled="loadingAction">Manter assinatura</button>
                    <button
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !text-white !bg-red-600 hover:!bg-red-700"
                        :disabled="loadingAction"
                        @click="confirmCancel"
                    >
                        {{ loadingAction ? 'Cancelando...' : 'Sim, cancelar assinatura' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import AuthService from '@/services/AuthService';
import { SubscriptionService, CompanyService, SessionService } from '@/services/Entity';
import AddCardModal from '@/components/AddCardModal.vue';
import UploadService from '@/services/FileService';
import { PLANS, getDefaultPlan } from '@/constants/plans';
import { adminBadgeClass } from '@/constants/adminBadge';

const MAX_LOGO_SIZE = process.env.VUE_APP_MAX_SIZE_PHOTO_BYTES;

export default {
    components: { AddCardModal },
    setup() {
        const authInfo = ref(AuthService.getAuthInfo() || {});
        const plan = getDefaultPlan();

        const subscriptionStatus = computed(() => authInfo.value.subscription_status || null);
        const daysLeft = computed(() => Number(authInfo.value.days_left) || 0);

        const canEditCompany = computed(() => AuthService.isOwnerOrAdmin());
        const company = ref({});
        const maxDevices = computed(() => {
            const found = PLANS.find(p => p.id === company.value.plan);
            return found ? found.maxDevices : 2;
        });
        const editingCompany = ref(false);
        const companyForm = ref({ name: '', phone: '', whatsapp: '', cep: '', address: '', responsavel_name: '', responsavel_crefito: '' });
        const savingCompany = ref(false);

        async function loadCompany() {
            if (!authInfo.value.company) return;
            try {
                const { data } = await CompanyService.getById(authInfo.value.company);
                company.value = data || {};
                resetCompanyForm();
            } catch (e) { /* silent */ }
        }

        function resetCompanyForm() {
            companyForm.value = {
                name: company.value.name || '',
                phone: company.value.phone || '',
                whatsapp: company.value.whatsapp || '',
                cep: company.value.cep || '',
                address: company.value.address || '',
                responsavel_name: company.value.responsavel_name || '',
                responsavel_crefito: company.value.responsavel_crefito || '',
            };
        }

        function startEditCompany() {
            resetCompanyForm();
            editingCompany.value = true;
        }

        function cancelEditCompany() {
            resetCompanyForm();
            editingCompany.value = false;
        }

        async function saveCompany() {
            savingCompany.value = true;
            try {
                await CompanyService.update({ id: company.value.id, ...companyForm.value });
                await loadCompany();
                editingCompany.value = false;
            } catch (e) {
                alert('Erro ao salvar dados da empresa.');
            } finally {
                savingCompany.value = false;
            }
        }

        const uploadingLogo = ref(false);

        async function onLogoFileChange(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;

            if (file.size > MAX_LOGO_SIZE) {
                alert('O limite máximo para imagens é 50mb.');
                return;
            }

            uploadingLogo.value = true;
            try {
                const { data } = await UploadService.upload(file, { folderType: 'logos' });
                const uploadedFile = data && data.success && data.success[0];
                if (!uploadedFile) return;

                await CompanyService.update({ id: company.value.id, logo: uploadedFile.id });
                await loadCompany();
                window.dispatchEvent(new CustomEvent('company-logo-updated', { detail: { logoUrl: company.value.logo_url } }));
            } catch (e) {
                alert('Erro ao salvar logo.');
            } finally {
                uploadingLogo.value = false;
            }
        }

        const subscription = ref(null);
        const loadingSubscription = ref(true);
        const loadingAction = ref(false);
        const retryingPayment = ref(false);

        const paymentMethods = ref([]);
        const loadingPaymentMethods = ref(false);
        const pmActionLoading = ref(false);

        const invoices = ref([]);
        const loadingInvoices = ref(false);

        const addCardModalRef = ref(null);

        const sessions = ref([]);
        const loadingSessions = ref(true);
        const currentDeviceId = AuthService.getOrCreateDeviceId();

        async function loadSessions() {
            loadingSessions.value = true;
            try {
                const { data } = await SessionService.list();
                sessions.value = Array.isArray(data) ? data : [];
            } catch (e) {
                sessions.value = [];
            } finally {
                loadingSessions.value = false;
            }
        }

        const logoutDeviceModalRef = ref(null);
        const deviceToLogout = ref(null);
        const loggingOutDevice = ref(false);
        const logoutDeviceError = ref('');

        function openLogoutDeviceModal(session) {
            deviceToLogout.value = session;
            logoutDeviceError.value = '';
            logoutDeviceModalRef.value?.showModal();
        }

        function closeLogoutDeviceModal() {
            logoutDeviceModalRef.value?.close();
            deviceToLogout.value = null;
        }

        async function confirmLogoutDevice() {
            if (!deviceToLogout.value) return;
            const deviceId = deviceToLogout.value.device_id;
            logoutDeviceError.value = '';
            loggingOutDevice.value = true;
            try {
                await SessionService.logoutDevice(deviceId);
                sessions.value = sessions.value.filter(s => s.device_id !== deviceId);
                closeLogoutDeviceModal();
            } catch (e) {
                logoutDeviceError.value = 'Erro ao desconectar dispositivo. Tente novamente.';
            } finally {
                loggingOutDevice.value = false;
            }
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            return new Date(dateStr).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
        }

        onMounted(async () => {
            await loadSubscription();
            await loadCompany();
            await loadSessions();
        });

        async function loadSubscription() {
            loadingSubscription.value = true;
            try {
                const { data } = await SubscriptionService.getActiveSubscription();
                subscription.value = data || null;
                if (data && data.active) {
                    loadPaymentMethods();
                    loadInvoices();
                }
                const tokenSaysSubscribed = ['active', 'past_due'].includes(authInfo.value.subscription_status);
                if (!!(data && data.active) !== tokenSaysSubscribed) {
                    syncAuthInfo();
                }
            } catch (e) {
                subscription.value = null;
            } finally {
                loadingSubscription.value = false;
            }
        }

        // O status guardado no login fica velho quando o Stripe muda a assinatura
        // (ex.: cancelou após as retentativas) — banner e guard de rota leem dele.
        async function syncAuthInfo() {
            try {
                await AuthService.refreshSession();
                authInfo.value = AuthService.getAuthInfo() || {};
            } catch (e) { /* silent */ }
        }

        async function loadPaymentMethods() {
            loadingPaymentMethods.value = true;
            try {
                const { data } = await SubscriptionService.listPaymentMethods();
                paymentMethods.value = Array.isArray(data) ? data : [];
            } catch (e) {
                paymentMethods.value = [];
            } finally {
                loadingPaymentMethods.value = false;
            }
        }

        async function loadInvoices() {
            loadingInvoices.value = true;
            try {
                const { data } = await SubscriptionService.listInvoices();
                invoices.value = Array.isArray(data) ? data : [];
            } catch (e) {
                invoices.value = [];
            } finally {
                loadingInvoices.value = false;
            }
        }

        const cancelModalRef = ref(null);

        function openCancelModal() {
            cancelModalRef.value?.showModal();
        }

        function closeCancelModal() {
            cancelModalRef.value?.close();
        }

        async function confirmCancel() {
            loadingAction.value = true;
            try {
                await SubscriptionService.cancelActiveSubscription();
                await loadSubscription();
                closeCancelModal();
            } catch (e) {
                alert('Erro ao cancelar. Tente novamente.');
            } finally {
                loadingAction.value = false;
            }
        }

        async function reactivate() {
            loadingAction.value = true;
            try {
                await SubscriptionService.reactivateSubscription();
                await loadSubscription();
            } catch (e) {
                alert('Erro ao reativar. Tente novamente.');
            } finally {
                loadingAction.value = false;
            }
        }

        async function retryPay() {
            retryingPayment.value = true;
            try {
                const { data } = await SubscriptionService.retryPayment();
                if (data && data.status === 'paid') {
                    await syncAuthInfo();
                    await loadSubscription();
                } else {
                    alert('Pagamento não processado. Verifique o cartão e tente novamente.');
                }
            } catch (e) {
                alert('Erro ao tentar pagamento. Verifique o cartão.');
            } finally {
                retryingPayment.value = false;
            }
        }

        async function setDefault(pmId) {
            pmActionLoading.value = true;
            try {
                await SubscriptionService.setDefaultPaymentMethod(pmId);
                await loadPaymentMethods();
            } catch (e) {
                alert('Erro ao definir cartão padrão.');
            } finally {
                pmActionLoading.value = false;
            }
        }

        function isLastCardOnActiveSubscription() {
            return paymentMethods.value.length === 1 && subscription.value && subscription.value.active;
        }

        async function removeCard(pmId) {
            if (!confirm('Remover este cartão?')) return;
            pmActionLoading.value = true;
            try {
                await SubscriptionService.removePaymentMethod(pmId);
                await loadPaymentMethods();
            } catch (e) {
                const msg = e?.response?.data?.message || 'Erro ao remover cartão.';
                alert(msg);
            } finally {
                pmActionLoading.value = false;
            }
        }

        function openAddCard() {
            addCardModalRef.value && addCardModalRef.value.openModal();
        }

        // Quem adiciona cartão quase sempre quer trocar o que está sendo cobrado.
        async function onCardAdded({ paymentMethodId } = {}) {
            if (paymentMethodId) {
                try {
                    await SubscriptionService.setDefaultPaymentMethod(paymentMethodId);
                } catch (e) { /* cartão ficou salvo; dá pra definir padrão pela lista */ }
            }
            await loadPaymentMethods();
            if (subscription.value && subscription.value.status === 'past_due') {
                await retryPay();
            }
        }

        const statusBadgeClass = computed(() => {
            const s = subscription.value && subscription.value.status;
            if (s === 'active') return adminBadgeClass('success');
            if (s === 'past_due') return adminBadgeClass('error');
            return adminBadgeClass('warning');
        });

        const statusLabel = computed(() => {
            const s = subscription.value && subscription.value.status;
            if (s === 'active') return subscription.value.cancel_at_period_end ? 'Cancelando' : 'Ativo';
            if (s === 'past_due') return 'Pagamento pendente';
            return s || '—';
        });

        function formatDate(unixTs) {
            if (!unixTs) return '—';
            return new Date(unixTs * 1000).toLocaleDateString('pt-BR');
        }

        function formatCouponLabel(coupon) {
            if (!coupon) return '';
            if (coupon.percent_off) return `${coupon.name} — ${coupon.percent_off}% off`;
            if (coupon.amount_off) return `${coupon.name} — R$ ${coupon.amount_off.toFixed(2)} off`;
            return coupon.name || 'Cupom ativo';
        }

        function invoiceStatusClass(status) {
            if (status === 'paid') return adminBadgeClass('success');
            if (status === 'open') return adminBadgeClass('warning');
            return adminBadgeClass('neutral');
        }

        function invoiceStatusLabel(status) {
            if (status === 'paid') return 'Pago';
            if (status === 'open') return 'Pendente';
            if (status === 'void') return 'Cancelado';
            return status || '—';
        }

        return {
            authInfo, subscriptionStatus, daysLeft, plan,
            canEditCompany, company, maxDevices, editingCompany, companyForm, savingCompany, saveCompany, uploadingLogo, onLogoFileChange,
            startEditCompany, cancelEditCompany,
            subscription, loadingSubscription, loadingAction, retryingPayment,
            paymentMethods, loadingPaymentMethods, pmActionLoading,
            invoices, loadingInvoices,
            addCardModalRef,
            sessions, loadingSessions, currentDeviceId, formatDateTime,
            logoutDeviceModalRef, deviceToLogout, loggingOutDevice, logoutDeviceError,
            openLogoutDeviceModal, closeLogoutDeviceModal, confirmLogoutDevice,
            statusBadgeClass, statusLabel,
            cancelModalRef, openCancelModal, closeCancelModal, confirmCancel, reactivate, retryPay,
            setDefault, removeCard, isLastCardOnActiveSubscription, openAddCard, onCardAdded, adminBadgeClass,
            formatDate, formatCouponLabel,
            invoiceStatusClass, invoiceStatusLabel
        };
    }
};
</script>
